import React from 'react';
import { BarChart2, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров инвестиции
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  investmentTypes
}) => {
  // Расчет текущей суммы инвестиции
  const initialInvestment = parseFloat(formData.initialInvestment) || 0;
  
  // Расчет текущего срока в годах
  const investmentTermYears = parseFloat(formData.investmentTerm) || 0;

  // Расчет процентной ставки
  const interestRate = parseFloat(formData.interestRate) || 0;
  
  // Обработчик изменения суммы инвестиции
  const handleInitialInvestmentChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      initialInvestment: newValue
    }));
  };
  
  // Обработчик изменения срока инвестиции
  const handleInvestmentTermChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      investmentTerm: newValue
    }));
  };
  
  // Обработчик изменения процентной ставки
  const handleInterestRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      interestRate: newValue
    }));
  };
  
  // Предопределенные суммы инвестиции для быстрого выбора
  const predefinedAmounts = [
    { value: '10000', label: '10 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' }
  ];
  
  // Предопределенные сроки инвестиции для быстрого выбора
  const predefinedTerms = [
    { value: '1', label: '1 год' },
    { value: '5', label: '5 лет' },
    { value: '10', label: '10 лет' },
    { value: '20', label: '20 лет' }
  ];
  
  // Функция расчета
  const calculateInvestment = () => {
    // Вызываем функцию основного расчета из родительского компонента
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Сумма инвестиции */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="initialInvestmentSlider" className="text-gray-600 dark:text-gray-400">Начальная сумма</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(initialInvestment, 0)}</span>
          </div>
          <input
            type="range"
            id="initialInvestmentSlider"
            min="10000"
            max="2000000"
            step="10000"
            value={initialInvestment}
            onChange={handleInitialInvestmentChange}
            aria-label="Начальная сумма инвестиции"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, initialInvestment: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.initialInvestment === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Срок инвестиции */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="investmentTermSlider" className="text-gray-600 dark:text-gray-400">Срок инвестиции</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {investmentTermYears} {pluralize(investmentTermYears, 'год', 'года', 'лет')}
            </span>
          </div>
          <input
            type="range"
            id="investmentTermSlider"
            min="1"
            max="40"
            step="1"
            value={investmentTermYears}
            onChange={handleInvestmentTermChange}
            aria-label="Срок инвестиции (лет)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными сроками */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedTerms.map(term => (
              <button
                key={term.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, investmentTerm: term.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.investmentTerm === term.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Процентная ставка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="interestRateSlider" className="text-gray-600 dark:text-gray-400">Процентная ставка</label>
            <span className="font-medium text-gray-900 dark:text-white">{interestRate}%</span>
          </div>
          <input
            type="range"
            id="interestRateSlider"
            min="0.5"
            max="20"
            step="0.5"
            value={interestRate}
            onChange={handleInterestRateChange}
            aria-label="Процентная ставка (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные процентные ставки (используем типы инвестиций) */}
          <div className="grid grid-cols-3 gap-1 mt-2">
            {investmentTypes.slice(0, 3).map(type => (
              <button
                key={type.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, interestRate: type.defaultRate.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${parseFloat(formData.interestRate) === type.defaultRate 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {type.name}: {type.defaultRate}%
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор частоты капитализации */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Капитализация</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, compoundingFrequency: 'monthly' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.compoundingFrequency === 'monthly' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Ежемесячно</div>
              <div className="text-xs opacity-75">12 раз в год</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, compoundingFrequency: 'annually' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.compoundingFrequency === 'annually' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Ежегодно</div>
              <div className="text-xs opacity-75">1 раз в год</div>
            </button>
          </div>
        </div>
        
        {/* Дополнительные взносы */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <label className="flex items-center mb-2">
            <input
              type="checkbox"
              checked={formData.includeAdditionalContributions}
              onChange={(e) => setFormData(prev => ({ ...prev, includeAdditionalContributions: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Регулярные взносы
            </span>
          </label>
          
          {formData.includeAdditionalContributions && (
            <div className="flex space-x-2">
              <input
                type="text"
                value={formData.additionalContribution}
                onChange={(e) => setFormData(prev => ({ ...prev, additionalContribution: e.target.value }))}
                className="form-input w-1/2 text-sm"
                placeholder="Сумма"
              />
              <select
                value={formData.contributionFrequency}
                onChange={(e) => setFormData(prev => ({ ...prev, contributionFrequency: e.target.value }))}
                className="form-select w-1/2 text-sm"
              >
                <option value="monthly">Ежемесячно</option>
                <option value="quarterly">Ежеквартально</option>
                <option value="annually">Ежегодно</option>
              </select>
            </div>
          )}
        </div>
        
        {/* Инфляция */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <label className="flex items-center mb-2">
            <input
              type="checkbox"
              checked={formData.adjustForInflation}
              onChange={(e) => setFormData(prev => ({ ...prev, adjustForInflation: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать инфляцию
            </span>
          </label>
          
          {formData.adjustForInflation && (
            <div className="flex space-x-2">
              <input
                type="text"
                value={formData.inflation}
                onChange={(e) => setFormData(prev => ({ ...prev, inflation: e.target.value }))}
                className="form-input w-full text-sm"
                placeholder="Процент инфляции"
              />
              <span className="flex items-center text-sm text-gray-700 dark:text-gray-300">%</span>
            </div>
          )}
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateInvestment}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать инвестицию
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;